import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  InputAdornment,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import { apiRequest } from '../utils/api';

const ORDER_TYPES = [
  { value: 'LIMIT', label: 'Limit' },
  { value: 'MARKET', label: 'Market' },
];

const DURATIONS = [
  { value: 'DAY', label: 'Day' },
  { value: 'GOOD_TILL_CANCEL', label: 'GTC' },
];

const SESSIONS = [
  { value: 'NORMAL', label: 'Normal' },
  { value: 'AM', label: 'AM' },
  { value: 'PM', label: 'PM' },
  { value: 'SEAMLESS', label: 'Seamless' },
];

export default function EquityOrderTest() {
  const [searchParams] = useSearchParams();

  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [accountsLoading, setAccountsLoading] = useState(true);

  const [symbol, setSymbol] = useState('');
  const [instruction, setInstruction] = useState('BUY');
  const [quantity, setQuantity] = useState(1);
  const [orderType, setOrderType] = useState('LIMIT');
  const [price, setPrice] = useState('');
  const [session, setSession] = useState('NORMAL');
  const [duration, setDuration] = useState('DAY');

  const [result, setResult] = useState(null);
  const [previewSpec, setPreviewSpec] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(null);

  // Prefill from manual review payload passed via query params
  useEffect(() => {
    const underlyingParam = searchParams.get('underlying');
    if (!underlyingParam) return;

    const instructionParam = searchParams.get('instruction') || 'BUY';
    const qty = parseInt(searchParams.get('quantity')) || 1;
    const orderTypeParam = (searchParams.get('orderType') || 'LIMIT').toUpperCase();
    const priceParam = searchParams.get('price') || '';
    const durationParam = searchParams.get('duration') || 'DAY';
    const tradeAccountId = searchParams.get('tradeAccountId') || '';

    setSymbol(underlyingParam);
    setInstruction(['BUY', 'SELL', 'BUY_TO_COVER', 'SELL_SHORT'].includes(instructionParam) ? instructionParam : 'BUY');
    setQuantity(qty);
    setOrderType(ORDER_TYPES.some(o => o.value === orderTypeParam) ? orderTypeParam : 'LIMIT');
    setPrice(orderTypeParam === 'MARKET' ? '' : priceParam);
    setDuration(DURATIONS.some(d => d.value === durationParam) ? durationParam : 'DAY');
    if (tradeAccountId) {
      setSelectedAccountId(tradeAccountId);
    }
  }, [searchParams]);

  // Fetch accounts (Schwab + Robinhood MCP)
  const fetchAccounts = useCallback(async () => {
    setAccountsLoading(true);
    try {
      const schwabResp = await apiRequest('/api/schwab-api/accounts');
      const schwabAccs = (schwabResp.data?.accounts || []).map(a => ({ ...a, accountType: 'schwab' }));

      let robinhoodAccs = [];
      try {
        const rhResp = await apiRequest('/api/mcp/robinhood/accounts');
        robinhoodAccs = (rhResp.data?.accounts || []).map(a => ({ ...a, accountType: 'robinhood-mcp' }));
      } catch (rhErr) {
        // Robinhood MCP may not be configured
      }

      // Fetch Robinhood direct-API accounts (stored OAuth token)
      let robinhoodDirectAccs = [];
      try {
        const allAccsResp = await apiRequest('/api/accounts');
        const rhDirect = (allAccsResp.data || allAccsResp || []).filter(
          a => ['robinhood', 'robinhood-api'].includes(String(a.apiType || a.api_type || '').toLowerCase())
        );
        robinhoodDirectAccs = rhDirect.map(a => ({
          id: String(a.id),
          name: a.name,
          accountType: 'robinhood',
        }));
      } catch (e) {
        // No Robinhood direct-API accounts
      }

      const allAccs = [...schwabAccs, ...robinhoodAccs, ...robinhoodDirectAccs];
      setAccounts(allAccs);
      if (allAccs.length > 0 && !selectedAccountId) {
        setSelectedAccountId(allAccs[0].id);
      }
    } catch (err) {
      setError('Failed to load accounts: ' + err.message);
    } finally {
      setAccountsLoading(false);
    }
  }, [selectedAccountId]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Fetch recent orders
  const fetchRecentOrders = useCallback(async () => {
    if (!selectedAccountId) return;
    setOrdersLoading(true);
    try {
      const selectedAcc = accounts.find(a => String(a.id) === String(selectedAccountId));
      const isRobinhoodMcp = selectedAcc?.accountType === 'robinhood-mcp';
      const isRobinhoodDirect = selectedAcc?.accountType === 'robinhood';
      let orders = [];
      if (isRobinhoodMcp) {
        const response = await apiRequest(`/api/mcp/robinhood/orders?accountId=${selectedAccountId}`);
        orders = response.data?.orders || [];
      } else if (isRobinhoodDirect) {
        const response = await apiRequest(`/api/robinhood-api/orders/${selectedAccountId}`);
        orders = response.data?.orders || [];
      } else {
        const response = await apiRequest(`/api/schwab-api/orders?accountId=${selectedAccountId}`);
        orders = response.data?.orders || [];
      }
      setRecentOrders(orders);
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setOrdersLoading(false);
    }
  }, [selectedAccountId, accounts]);

  useEffect(() => {
    if (selectedAccountId) {
      fetchRecentOrders();
    }
  }, [selectedAccountId, fetchRecentOrders]);

  const buildLegs = () => {
    if (!symbol) return null;
    return [{
      symbol: symbol.trim().toUpperCase(),
      instruction,
      quantity: parseInt(quantity) || 1,
      assetType: 'EQUITY'
    }];
  };

  const isWorkingOrderStatus = (status) => {
    if (!status) return false;
    return ['WORKING', 'PENDING', 'QUEUED', 'OPEN', 'AWAITING', 'CONFIRMED', 'UNFILLED', 'RECEIVED', 'AWAITING_PARENT', 'AWAITING_CONDITION'].includes(String(status).toUpperCase());
  };

  const fetchQuote = useCallback(async () => {
    const sym = symbol.trim().toUpperCase();
    if (!sym) {
      setQuote(null);
      setQuoteError(null);
      return;
    }
    setQuoteLoading(true);
    setQuoteError(null);
    try {
      let q = null;
      // Prefer the selected account's quote provider (Schwab/Tradier)
      if (selectedAccountId) {
        try {
          const resp = await apiRequest(`/api/accounts/${selectedAccountId}/quotes`, {
            method: 'POST',
            body: JSON.stringify({ symbols: [sym] }),
          });
          const arr = resp.data?.quotes?.quotes?.quote || [];
          q = Array.isArray(arr) ? (arr.find(qq => (qq.symbol || '').toUpperCase() === sym) || arr[0]) : null;
        } catch (e) {
          // fall through
        }
      }
      if (!q) {
        try {
          const resp = await apiRequest(`/api/mcp/equity-quotes?symbols=${sym}`);
          q = (resp.data?.quotes || [])[0];
        } catch (e) {
          // no fallback available
        }
      }
      if (q) {
        const last = parseFloat(q.last_trade_price ?? q.last_price ?? q.last ?? q.mark_price ?? q.mark ?? q.close ?? 0) || 0;
        const bid = parseFloat(q.bid_price ?? q.bid ?? 0) || 0;
        const ask = parseFloat(q.ask_price ?? q.ask ?? 0) || 0;
        const mid = bid && ask ? (bid + ask) / 2 : last;
        setQuote({ last, bid, ask, mid, symbol: (q.symbol || sym).toUpperCase() });
      } else {
        setQuote(null);
        setQuoteError('No quote available');
      }
    } catch (err) {
      console.warn('Quote fetch error:', err);
      setQuoteError('Failed to load quote');
      setQuote(null);
    } finally {
      setQuoteLoading(false);
    }
  }, [symbol, selectedAccountId]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (symbol.trim()) {
        fetchQuote();
      } else {
        setQuote(null);
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [symbol, fetchQuote]);

  const handlePreview = async () => {
    setError(null);
    setResult(null);
    setPreviewSpec(null);

    const legs = buildLegs();
    if (!legs) {
      setError('Please enter a symbol');
      return;
    }

    setLoading(true);
    try {
      const isPricedType = orderType === 'LIMIT';
      const body = {
        legs,
        orderType,
        price: isPricedType && price && !isNaN(parseFloat(price)) ? parseFloat(price) : undefined,
        session,
        duration,
      };
      const response = await apiRequest('/api/schwab-api/orders/preview', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setPreviewSpec(response.data?.orderSpec);
    } catch (err) {
      setError('Preview failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceOrder = async () => {
    setError(null);
    setResult(null);
    setPreviewSpec(null);

    if (!selectedAccountId) {
      setError('Please select an account');
      return;
    }

    const legs = buildLegs();
    if (!legs) {
      setError('Please enter a symbol');
      return;
    }

    setLoading(true);
    try {
      const isPricedType = orderType === 'LIMIT';
      const selectedAcc = accounts.find(a => String(a.id) === String(selectedAccountId));
      const isRobinhoodMcp = selectedAcc?.accountType === 'robinhood-mcp';
      const isRobinhoodDirect = selectedAcc?.accountType === 'robinhood';

      let response;
      if (isRobinhoodMcp) {
        const rhBody = {
          accountId: selectedAccountId,
          legs,
          orderType,
          price: isPricedType && price && !isNaN(parseFloat(price)) ? parseFloat(price) : undefined,
          timeInForce: duration === 'DAY' ? 'gfd' : 'gtc',
        };
        response = await apiRequest('/api/mcp/robinhood/orders/place', {
          method: 'POST',
          body: JSON.stringify(rhBody),
        });
      } else if (isRobinhoodDirect) {
        const rhDirectBody = {
          accountId: selectedAccountId,
          legs,
          orderType,
          price: isPricedType && price && !isNaN(parseFloat(price)) ? parseFloat(price) : undefined,
          timeInForce: duration === 'DAY' ? 'gfd' : 'gtc',
        };
        response = await apiRequest(`/api/robinhood-api/orders/place/${selectedAccountId}`, {
          method: 'POST',
          body: JSON.stringify(rhDirectBody),
        });
      } else {
        const body = {
          accountId: selectedAccountId,
          legs,
          orderType,
          price: isPricedType && price && !isNaN(parseFloat(price)) ? parseFloat(price) : undefined,
          session,
          duration,
        };
        response = await apiRequest('/api/schwab-api/orders/place', {
          method: 'POST',
          body: JSON.stringify(body),
        });
      }

      setResult(response.data);
      if (response.data?.success) {
        fetchRecentOrders();
      } else {
        const detail = response.data?.message || response.data?.error || 'Unknown error';
        setError('Order placement failed: ' + detail);
      }
    } catch (err) {
      setError('Order placement failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (!selectedAccountId || !orderId) return;
    try {
      const selectedAcc = accounts.find(a => String(a.id) === String(selectedAccountId));
      const isRobinhoodMcp = selectedAcc?.accountType === 'robinhood-mcp';
      const isRobinhoodDirect = selectedAcc?.accountType === 'robinhood';
      if (isRobinhoodMcp) {
        await apiRequest(`/api/mcp/robinhood/orders/${orderId}/cancel`, {
          method: 'POST',
        });
      } else if (isRobinhoodDirect) {
        // Robinhood direct-API cancel not yet implemented
        setError('Cancel not supported for Robinhood direct-API accounts');
        return;
      } else {
        await apiRequest(`/api/schwab-api/orders/${orderId}`, {
          method: 'DELETE',
          body: JSON.stringify({ accountId: selectedAccountId }),
        });
      }
      fetchRecentOrders();
    } catch (err) {
      setError('Cancel failed: ' + (err.message || 'Unknown error'));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="account-label">Account</InputLabel>
                <Select
                  labelId="account-label"
                  value={selectedAccountId}
                  label="Account"
                  onChange={(e) => setSelectedAccountId(e.target.value)}
                  disabled={accountsLoading}
                >
                  {accounts.map(a => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.displayName || a.accountNumber || a.id} ({a.accountType})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="instruction-label">Side</InputLabel>
                <Select
                  labelId="instruction-label"
                  value={instruction}
                  label="Side"
                  onChange={(e) => setInstruction(e.target.value)}
                >
                  <MenuItem value="BUY">Buy</MenuItem>
                  <MenuItem value="SELL">Sell</MenuItem>
                  <MenuItem value="BUY_TO_COVER">Buy to Cover</MenuItem>
                  <MenuItem value="SELL_SHORT">Sell Short</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Symbol"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                placeholder="e.g. AAPL"
                helperText={quoteLoading ? 'Loading quote...' : quoteError || (quote ? `Bid ${quote.bid.toFixed(2)} / Mid ${quote.mid.toFixed(2)} / Ask ${quote.ask.toFixed(2)} / Last ${quote.last.toFixed(2)}` : '')}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 0))}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="order-type-label">Order Type</InputLabel>
                <Select
                  labelId="order-type-label"
                  value={orderType}
                  label="Order Type"
                  onChange={(e) => setOrderType(e.target.value)}
                >
                  {ORDER_TYPES.map(t => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Price"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                disabled={orderType === 'MARKET'}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="session-label">Session</InputLabel>
                <Select
                  labelId="session-label"
                  value={session}
                  label="Session"
                  onChange={(e) => setSession(e.target.value)}
                >
                  {SESSIONS.map(s => (
                    <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="duration-label">Duration</InputLabel>
                <Select
                  labelId="duration-label"
                  value={duration}
                  label="Duration"
                  onChange={(e) => setDuration(e.target.value)}
                >
                  {DURATIONS.map(d => (
                    <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={handlePreview}
              disabled={loading}
            >
              Preview
            </Button>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              onClick={handlePlaceOrder}
              disabled={loading || !selectedAccountId}
            >
              Place Order
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchRecentOrders}
              disabled={!selectedAccountId || ordersLoading}
            >
              Refresh Orders
            </Button>
          </Box>
        </CardContent>
      </Card>

      {symbol.trim() && (
        <Card sx={{ mb: 3, bgcolor: 'background.paper' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">
                {quote ? `Live Quote — ${quote.symbol}` : 'Live Quote'}
              </Typography>
              {quoteLoading && <CircularProgress size={20} />}
            </Box>
            {quoteLoading && !quote && <Typography variant="body2">Loading quote…</Typography>}
            {!quoteLoading && quoteError && <Alert severity="warning" sx={{ py: 0.5 }}>{quoteError}</Alert>}
            {quote && (
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Bid</Typography>
                  <Typography variant="h6">{quote.bid.toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Mid</Typography>
                  <Typography variant="h6">{quote.mid.toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Ask</Typography>
                  <Typography variant="h6">{quote.ask.toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Last</Typography>
                  <Typography variant="h6">{quote.last.toFixed(2)}</Typography>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      )}

      {previewSpec && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Order Preview
            </Typography>
            <pre style={{ overflow: 'auto', maxHeight: 300 }}>
              {JSON.stringify(previewSpec, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      {result && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Result
            </Typography>
            <pre style={{ overflow: 'auto', maxHeight: 300 }}>
              {JSON.stringify(result, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Orders
          </Typography>
          {ordersLoading ? (
            <CircularProgress />
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Symbol</TableCell>
                    <TableCell>Side</TableCell>
                    <TableCell>Qty</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No recent orders
                      </TableCell>
                    </TableRow>
                  ) : (
                    recentOrders.map((order) => (
                      <TableRow key={order.orderId || order.id}>
                        <TableCell>{order.orderId || order.id}</TableCell>
                        <TableCell>{order.symbol || order.underlying || '-'}</TableCell>
                        <TableCell>{order.instruction || order.side || '-'}</TableCell>
                        <TableCell>{order.quantity || '-'}</TableCell>
                        <TableCell>{order.price || order.activePrice || '-'}</TableCell>
                        <TableCell>{order.status || '-'}</TableCell>
                        <TableCell>
                          {isWorkingOrderStatus(order.status) && (
                            <Button
                              size="small"
                              onClick={() => handleCancelOrder(order.orderId || order.id)}
                            >
                              Cancel
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
